/**
 * Inline Diff Session (Cursor-style review)
 *
 * Instead of applying AI changes all at once, materialize them into the editor
 * as a reviewable diff: original lines marked red, proposed lines marked green,
 * with per-hunk ✓ Accept / ✗ Reject controls rendered inline (Monaco view zones).
 *
 * A "hunk" maps naturally to a question block in QSyntax (split by === / ---),
 * so the teacher can keep some generated questions and drop others.
 *
 * The editor is read-only during review so line tracking stays deterministic;
 * decorations track line ranges across accept/reject operations.
 */

import * as monaco from 'monaco-editor'
import { AiChange } from './ai-sidebar-chat'

type Resolution = 'accepted' | 'rejected' | null

interface DiffHunk {
  id: string
  type: 'replace' | 'insert'
  label: string
  oldDecoId: string | null // decoration over original lines (replace only)
  newDecoId: string // decoration over proposed lines
  resolved: Resolution
  zoneId: string | null
}

const OVERLAY_WIDGET_ID = 'ai-inline-diff-overlay'
const STYLE_ID = 'ai-inline-diff-styles'

export class InlineDiffSession {
  private editor: monaco.editor.IStandaloneCodeEditor
  private model: monaco.editor.ITextModel
  private hunks: DiffHunk[] = []
  private prevReadOnly: boolean
  private keydownDisposable: monaco.IDisposable | null = null
  private overlayNode: HTMLElement | null = null
  private overlayWidget: monaco.editor.IOverlayWidget | null = null
  private onAllResolved: (anyAccepted: boolean) => void
  private disposed = false

  constructor(
    editor: monaco.editor.IStandaloneCodeEditor,
    onAllResolved: (anyAccepted: boolean) => void
  ) {
    this.editor = editor
    this.model = editor.getModel() as monaco.editor.ITextModel
    this.prevReadOnly = !!editor.getOption(monaco.editor.EditorOption.readOnly)
    this.onAllResolved = onAllResolved
  }

  /** Returns true if a reviewable diff was started */
  start(changes: AiChange[]): boolean {
    if (!this.model || changes.length === 0) return false
    ensureStyles()

    // Mark the model as a scratch buffer so MonacoLazyComponent's change handler
    // does not sync proposed (green) lines to the store during review.
    ;(this.editor as any).__aiDiffReviewing = true

    const originalLines = this.model.getValue().split('\n')
    const sorted = [...changes].sort((a, b) => a.startLine - b.startLine)

    // Build the merged document (original + proposed shown together) and record
    // the new-document line ranges of each old/new block.
    const out: string[] = []
    let src = 0 // 0-based pointer into originalLines
    type Block = { type: 'replace' | 'insert'; label: string; oldStart: number; oldEnd: number; newStart: number; newEnd: number }
    const blocks: Block[] = []

    const pushLines = (arr: string[]) => { for (const l of arr) out.push(l) }

    for (const change of sorted) {
      const targetOrig = Math.max(0, Math.min(change.startLine - 1, originalLines.length))
      // emit untouched original lines up to the change
      while (src < targetOrig) { out.push(originalLines[src]); src++ }

      const label = `問${questionOrdinal(originalLines, change.startLine)}`
      const contentLines = change.content.split('\n')

      if (change.type === 'replace') {
        const endOrig = Math.max(targetOrig, Math.min(change.endLine, originalLines.length))
        const oldStart = out.length + 1
        for (let i = targetOrig; i < endOrig; i++) out.push(originalLines[i])
        const oldEnd = out.length
        src = endOrig
        const newStart = out.length + 1
        pushLines(contentLines)
        const newEnd = out.length
        blocks.push({ type: 'replace', label, oldStart, oldEnd, newStart, newEnd })
      } else {
        // When the anchor line is blank (cursor on an empty line / empty document),
        // a leading === / --- separator would create an empty preceding question.
        // Drop leading separators (and blanks) in that case.
        const anchorIdx = change.startLine - 1
        const anchorBlank = anchorIdx < 0 || anchorIdx >= originalLines.length || originalLines[anchorIdx].trim() === ''
        let insertLines = contentLines
        if (anchorBlank) {
          let s = 0
          while (s < insertLines.length) {
            const t = insertLines[s].trim()
            if (t === '' || /^={3,}$/.test(t) || /^-{3,}$/.test(t)) s++
            else break
          }
          insertLines = insertLines.slice(s)
        }
        // insert: keep the anchor line, show proposed content right after it
        if (src < originalLines.length && src === change.startLine - 1) { out.push(originalLines[src]); src++ }
        const newStart = out.length + 1
        pushLines(insertLines)
        const newEnd = out.length
        blocks.push({ type: 'insert', label, oldStart: 0, oldEnd: 0, newStart, newEnd })
      }
    }
    while (src < originalLines.length) { out.push(originalLines[src]); src++ }

    // Replace whole document in a single undoable edit
    const fullRange = this.model.getFullModelRange()
    this.editor.executeEdits('ai-diff-start', [{ range: fullRange, text: out.join('\n'), forceMoveMarkers: true }])

    // Create decorations + hunk records
    for (let i = 0; i < blocks.length; i++) {
      const b = blocks[i]
      const decos: monaco.editor.IModelDeltaDecoration[] = []
      if (b.type === 'replace' && b.oldEnd >= b.oldStart) {
        decos.push(lineDeco(b.oldStart, b.oldEnd, 'ai-diff-old', 'ai-diff-gutter-old'))
      }
      decos.push(lineDeco(b.newStart, b.newEnd, 'ai-diff-new', 'ai-diff-gutter-new'))
      const ids = this.editor.deltaDecorations([], decos)
      const newDecoId = ids[ids.length - 1]
      const oldDecoId = b.type === 'replace' ? ids[0] : null
      this.hunks.push({
        id: `hunk_${i}`,
        type: b.type,
        label: b.label,
        oldDecoId,
        newDecoId,
        resolved: null,
        zoneId: null
      })
    }

    // Lock editing during review for deterministic line tracking
    this.editor.updateOptions({ readOnly: true })
    this.relayoutZones()
    this.mountOverlay()

    this.keydownDisposable = this.editor.onKeyDown((e) => {
      if (e.keyCode === monaco.KeyCode.Escape) { e.preventDefault(); e.stopPropagation(); this.rejectAll() }
      else if ((e.metaKey || e.ctrlKey) && e.keyCode === monaco.KeyCode.Enter) { e.preventDefault(); e.stopPropagation(); this.acceptAll() }
    })

    return true
  }

  acceptAll(): void {
    // Resolve bottom-first so earlier hunks keep valid ranges
    this.pendingBottomFirst().forEach(h => this.resolve(h, true))
    this.finishIfDone()
  }

  rejectAll(): void {
    this.pendingBottomFirst().forEach(h => this.resolve(h, false))
    this.finishIfDone()
  }

  /** Tear down without modifying text further (keeps current document state) */
  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.keydownDisposable?.dispose()
    this.keydownDisposable = null
    this.editor.changeViewZones(acc => {
      for (const h of this.hunks) if (h.zoneId) { acc.removeZone(h.zoneId); h.zoneId = null }
    })
    const allDecoIds = this.hunks.flatMap(h => [h.oldDecoId, h.newDecoId].filter(Boolean) as string[])
    if (allDecoIds.length) this.editor.deltaDecorations(allDecoIds, [])
    if (this.overlayWidget) { this.editor.removeOverlayWidget(this.overlayWidget); this.overlayWidget = null }
    this.overlayNode = null
    this.editor.updateOptions({ readOnly: this.prevReadOnly })
    // Review finished: model changes sync to the store again. The explicit
    // onContentChange on accept (in onAllResolved) commits the accepted result.
    ;(this.editor as any).__aiDiffReviewing = false
  }

  // --------------------------------------------------------------------------

  private pendingBottomFirst(): DiffHunk[] {
    return this.hunks
      .filter(h => !h.resolved)
      .map(h => ({ h, line: this.model.getDecorationRange(h.newDecoId)?.startLineNumber || 0 }))
      .sort((a, b) => b.line - a.line)
      .map(x => x.h)
  }

  private resolve(hunk: DiffHunk, accept: boolean): void {
    if (hunk.resolved) return

    if (hunk.type === 'replace') {
      // accept -> drop the OLD block; reject -> drop the NEW block
      const decoId = accept ? hunk.oldDecoId : hunk.newDecoId
      if (decoId) this.deleteDecoLines(decoId)
    } else if (!accept) {
      // insert + reject -> drop the proposed block (accept keeps it as-is)
      this.deleteDecoLines(hunk.newDecoId)
    }

    const ids = [hunk.oldDecoId, hunk.newDecoId].filter(Boolean) as string[]
    if (ids.length) this.editor.deltaDecorations(ids, [])
    hunk.oldDecoId = null
    hunk.resolved = accept ? 'accepted' : 'rejected'
  }

  private deleteDecoLines(decoId: string): void {
    const r = this.model.getDecorationRange(decoId)
    if (!r) return
    // Edit via the model, not editor.executeEdits: the editor is readOnly during
    // review and executeEdits is a no-op while readOnly (so reject would not remove
    // the proposed lines). model.pushEditOperations bypasses the editor readOnly.
    this.model.pushEditOperations(
      [],
      [{ range: lineDeleteRange(this.model, r.startLineNumber, r.endLineNumber), text: '' }],
      () => null
    )
  }

  private acceptOne(hunk: DiffHunk): void {
    this.resolve(hunk, true)
    this.relayoutZones()
    this.updateOverlay()
    this.finishIfDone()
  }

  private rejectOne(hunk: DiffHunk): void {
    this.resolve(hunk, false)
    this.relayoutZones()
    this.updateOverlay()
    this.finishIfDone()
  }

  private finishIfDone(): void {
    if (this.hunks.some(h => !h.resolved)) {
      this.relayoutZones()
      this.updateOverlay()
      return
    }
    const anyAccepted = this.hunks.some(h => h.resolved === 'accepted')
    const wasReadOnly = this.prevReadOnly
    this.dispose()
    this.editor.updateOptions({ readOnly: wasReadOnly })
    this.onAllResolved(anyAccepted)
  }

  private relayoutZones(): void {
    this.editor.changeViewZones(acc => {
      for (const h of this.hunks) {
        if (h.zoneId) { acc.removeZone(h.zoneId); h.zoneId = null }
      }
      for (const h of this.hunks) {
        if (h.resolved) continue
        const anchorId = h.type === 'replace' ? (h.oldDecoId || h.newDecoId) : h.newDecoId
        const r = this.model.getDecorationRange(anchorId)
        if (!r) continue
        h.zoneId = acc.addZone({
          afterLineNumber: Math.max(0, r.startLineNumber - 1),
          heightInPx: 32,
          domNode: this.buildHunkHeader(h)
        })
      }
    })
  }

  private buildHunkHeader(hunk: DiffHunk): HTMLElement {
    const bar = document.createElement('div')
    bar.className = 'ai-diff-zone'

    const label = document.createElement('span')
    label.className = 'ai-diff-zone__label'
    label.textContent = `${hunk.type === 'replace' ? '📝' : '➕'} ${hunk.label}`
    bar.appendChild(label)

    const accept = document.createElement('button')
    accept.className = 'ai-diff-btn ai-diff-btn--accept'
    accept.textContent = '✓ 採用'
    accept.onclick = (e) => { e.stopPropagation(); this.acceptOne(hunk) }
    bar.appendChild(accept)

    const reject = document.createElement('button')
    reject.className = 'ai-diff-btn ai-diff-btn--reject'
    reject.textContent = '✗ 却下'
    reject.onclick = (e) => { e.stopPropagation(); this.rejectOne(hunk) }
    bar.appendChild(reject)

    return bar
  }

  private mountOverlay(): void {
    const node = document.createElement('div')
    node.className = 'ai-diff-overlay'
    this.overlayNode = node
    this.overlayWidget = {
      getId: () => OVERLAY_WIDGET_ID,
      getDomNode: () => node,
      getPosition: () => ({ preference: monaco.editor.OverlayWidgetPositionPreference.BOTTOM_RIGHT_CORNER })
    }
    this.editor.addOverlayWidget(this.overlayWidget)
    this.updateOverlay()
  }

  private updateOverlay(): void {
    if (!this.overlayNode) return
    const pending = this.hunks.filter(h => !h.resolved).length
    this.overlayNode.innerHTML = ''

    const count = document.createElement('span')
    count.className = 'ai-diff-overlay__count'
    count.textContent = `${pending}件の変更 · ⌘⏎ すべて採用 · Esc すべて却下`
    this.overlayNode.appendChild(count)

    const acceptAll = document.createElement('button')
    acceptAll.className = 'ai-diff-btn ai-diff-btn--accept'
    acceptAll.textContent = '✓ すべて採用'
    acceptAll.onclick = (e) => { e.stopPropagation(); this.acceptAll() }
    this.overlayNode.appendChild(acceptAll)

    const rejectAll = document.createElement('button')
    rejectAll.className = 'ai-diff-btn ai-diff-btn--reject'
    rejectAll.textContent = '✗ すべて却下'
    rejectAll.onclick = (e) => { e.stopPropagation(); this.rejectAll() }
    this.overlayNode.appendChild(rejectAll)
  }
}

// ============================================================================
// Helpers
// ============================================================================

function lineDeco(start: number, end: number, className: string, gutterClass: string): monaco.editor.IModelDeltaDecoration {
  return {
    range: new monaco.Range(start, 1, end, 1),
    options: {
      isWholeLine: true,
      className,
      linesDecorationsClassName: gutterClass,
      stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges
    }
  }
}

/** Range that deletes whole lines [a..b] (1-based, inclusive), including a line break */
function lineDeleteRange(model: monaco.editor.ITextModel, a: number, b: number): monaco.Range {
  const lineCount = model.getLineCount()
  if (b >= lineCount) {
    if (a > 1) return new monaco.Range(a - 1, model.getLineMaxColumn(a - 1), b, model.getLineMaxColumn(b))
    return new monaco.Range(a, 1, b, model.getLineMaxColumn(b))
  }
  return new monaco.Range(a, 1, b + 1, 1)
}

/** Question ordinal = number of QSyntax separators (=== / ---) before the line, + 1 */
function questionOrdinal(originalLines: string[], startLine: number): number {
  let n = 1
  for (let i = 0; i < startLine - 1 && i < originalLines.length; i++) {
    const t = originalLines[i].trim()
    if (t === '===' || t === '---') n++
  }
  return n
}

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return
  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = `
.ai-diff-new { background: rgba(46, 160, 67, 0.18); }
.ai-diff-old { background: rgba(248, 81, 73, 0.16); }
.ai-diff-gutter-new::before { content: '+'; color: #3fb950; font-weight: 700; margin-left: 4px; }
.ai-diff-gutter-old::before { content: '-'; color: #f85149; font-weight: 700; margin-left: 4px; }
.ai-diff-zone {
  display: flex; align-items: center; gap: 10px;
  height: 100%; padding: 0 12px;
  background: #252830; border-left: 3px solid #3fb950;
  font-size: 12px; color: #c5c8c6;
  position: relative; z-index: 6; pointer-events: auto;
}
.ai-diff-zone__label { font-weight: 600; margin-right: 4px; display: inline-flex; align-items: center; }
.ai-diff-btn {
  display: inline-flex; align-items: center; justify-content: center;
  box-sizing: border-box; height: 22px;
  border: 1px solid transparent; border-radius: 4px;
  padding: 0 12px; font-size: 12px; font-weight: 600; cursor: pointer;
  line-height: 1; background: transparent;
  pointer-events: auto; white-space: nowrap; vertical-align: middle;
}
.ai-diff-btn--accept { color: #3fb950; border-color: #2ea043; }
.ai-diff-btn--accept:hover { background: rgba(46, 160, 67, 0.18); }
.ai-diff-btn--reject { color: #f85149; border-color: #da3633; margin-left: 5px; }
.ai-diff-btn--reject:hover { background: rgba(248, 81, 73, 0.16); }
.ai-diff-overlay {
  display: flex; align-items: center; gap: 8px;
  margin: 8px; padding: 6px 10px;
  background: #1d1f21; border: 1px solid #3a3f47; border-radius: 6px;
  box-shadow: 0 4px 14px rgba(0,0,0,0.4);
}
.ai-diff-overlay__count { font-size: 11px; color: #8b949e; margin-right: 4px; }
`
  document.head.appendChild(style)
}
